<template>
  <Card
    class="flex h-full min-w-0 w-full flex-col overflow-hidden rounded-[1.25rem] border border-[#d8e1f0] bg-white text-[#1f2937] shadow-[0_18px_40px_rgba(15,23,42,0.06)]"
  >
    <CardHeader
      class="border-b border-[#e5ecf5] bg-[#f8fbff]"
      :class="comfortMode === 'comfortable' ? 'px-6 md:px-8 py-5 md:py-6' : 'px-6 py-5'"
    >
      <div
        class="flex items-start justify-between gap-4 max-[640px]:flex-col max-[640px]:items-stretch"
      >
        <div class="space-y-1">
          <CardTitle
            :class="
              cn(
                'm-0 font-black text-[#0f172a]',
                comfortMode === 'comfortable' ? 'text-lg md:text-xl' : 'text-[1.05rem] font-extrabold'
              )
            "
          >
            {{ role === 'client' ? 'Work Summary' : 'Workload by Status' }}
          </CardTitle>
          <CardDescription
            :class="
              cn(
                'font-medium mt-1 text-[#64748b]',
                comfortMode === 'comfortable' ? 'text-sm md:text-base' : 'text-[0.88rem]'
              )
            "
          >
            {{ role === 'client' ? 'A quick view of completed, active, and pending work across your services.' : chartDescription }}
          </CardDescription>
        </div>
      </div>
    </CardHeader>

    <CardContent class="flex-1 p-0">
      <div
        v-if="loading"
        class="space-y-4 p-6"
      >
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
          <Skeleton
            v-for="n in 5"
            :key="n"
            class="h-[4.75rem] rounded-[1rem] bg-[#eef4ff]"
          />
        </div>
        <Skeleton class="h-[20rem] rounded-[1.15rem] bg-[#eef4ff]" />
      </div>

      <div
        v-else-if="!hasData"
        class="px-6 py-6"
      >
        <div
          class="grid min-h-[24rem] place-items-center rounded-[1.15rem] border border-dashed border-[#dbe5f2] bg-[#fafcff] px-6 text-center"
        >
          <div class="space-y-2">
            <p class="text-[1rem] font-bold text-[#0f172a]">
              No workload data yet
            </p>
            <p class="text-[0.92rem] leading-7 text-[#64748b] font-medium">
              Workload metrics will appear once task and lead activity is available.
            </p>
          </div>
        </div>
      </div>

      <div
        v-else
        class="p-6 text-left"
        :class="comfortMode === 'comfortable' ? 'space-y-6 md:p-8' : 'space-y-5 p-6'"
      >
        <div
          class="rounded-[1.1rem] border border-[#dbe5f2] bg-white p-5"
        >
          <div class="mb-4 flex items-center justify-between gap-3">
            <p
              :class="
                cn(
                  'font-bold uppercase tracking-[0.14em] text-[#64748b]',
                  comfortMode === 'comfortable' ? 'text-xs md:text-sm' : 'text-[0.72rem]'
                )
              "
            >
              Tasks
            </p>
            <p
              :class="
                cn(
                  'font-medium text-[#64748b]',
                  comfortMode === 'comfortable' ? 'text-xs md:text-sm' : 'text-[0.78rem]'
                )
              "
            >
              Status distribution across the current scope
            </p>
          </div>
          <div
            :class="comfortMode === 'comfortable' ? 'h-[25rem]' : 'h-[20rem]'"
          >
            <canvas ref="canvasRef" />
          </div>
        </div>

        <div
          v-if="role === 'client'"
          class="rounded-2xl border border-blue-100 bg-[#eff6ff]/35"
          :class="comfortMode === 'comfortable' ? 'p-5 md:p-6' : 'p-3.5'"
        >
          <p
            :class="
              cn(
                'font-extrabold uppercase tracking-wider text-[#1e40af] mb-1.5',
                comfortMode === 'comfortable' ? 'text-xs md:text-sm' : 'text-[0.72rem]'
              )
            "
          >
            Insight
          </p>
          <p
            :class="
              cn(
                'font-semibold text-slate-700 leading-relaxed',
                comfortMode === 'comfortable' ? 'text-sm md:text-base' : 'text-[0.82rem]'
              )
            "
          >
            Most active work is currently focused on onboarding and optimization of your core services.
          </p>
        </div>
      </div>
    </CardContent>
  </Card>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, nextTick, ref, watch, inject } from "vue";
import Chart from "chart.js/auto";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import type {
  DashboardSummary,
  DashboardWorkloadItem,
} from "@/types/dashboard";

const props = defineProps<{
  data: DashboardWorkloadItem[];
  loading?: boolean;
  role?: string | null;
  summary?: DashboardSummary | null;
}>();

const comfortMode = inject<any>("comfortMode", ref("comfortable"));

const canvasRef = ref<HTMLCanvasElement | null>(null);
let chart: Chart | null = null;

const statusCount = computed(() => {
  const counts = new Map<string, number>();

  for (const item of props.data ?? []) {
    counts.set(normalizeStatus(item.status), item.count);
  }

  return counts;
});

const totalTasksCount = computed(() => {
  return props.summary?.total_tasks ?? props.summary?.totalTickets ?? 0;
});

const chartData = computed(() => {
  if (props.role === "client") {
    return [
      {
        label: "Completed",
        value: sumCounts(["completed", "done"]),
        color: "#10b981",
      },
      {
        label: "In Progress",
        value: sumCounts(["ongoing", "in_progress", "for_qa"]),
        color: "#3b82f6",
      },
      {
        label: "Pending",
        value: sumCounts(["not_started", "pending", "hold"]),
        color: "#94a3b8",
      },
    ];
  }

  return [
    {
      label: "Completed",
      value: sumCounts(["completed", "done"]),
      color: "#10b981",
    },
    {
      label: "In Progress",
      value: sumCounts(["ongoing", "in_progress", "for_qa"]),
      color: "#635bff",
    },
    {
      label: "Pending",
      value: sumCounts(["not_started", "pending", "hold"]),
      color: "#ef4444",
    },
  ];
});

const hasData = computed(() => totalTasksCount.value > 0 || chartData.value.some((item) => item.value > 0));

const chartDescription = computed(() =>
  String(props.role || "").toLowerCase() === "client"
    ? "A streamlined view of work progress, completions, and new lead activity."
    : "A streamlined view of work progress, completions, and new lead activity."
);

function normalizeStatus(status: string) {
  return String(status || "")
    .trim()
    .toLowerCase()
    .replace(/[-\s]+/g, "_");
}

function sumCounts(statuses: string[]) {
  return statuses.reduce(
    (sum, key) => sum + (statusCount.value.get(key) ?? 0),
    0
  );
}

function destroyChart() {
  chart?.destroy();
  chart = null;
}

function renderChart() {
  if (!canvasRef.value || !hasData.value) return;

  destroyChart();

  const labels = chartData.value.map((item) => item.label);
  const values = chartData.value.map((item) => item.value);
  const colors = chartData.value.map((item) => item.color);

  const isComfortable = comfortMode.value === "comfortable";
  const tickFontSize = isComfortable ? 13 : 11;
  const valueFontSize = isComfortable ? 13 : 11;

  chart = new Chart(canvasRef.value, {
    type: "bar",
    data: {
      labels,
      datasets: [
        {
          label: "Tasks",
          data: values,
          backgroundColor: colors,
          borderRadius: 10,
          barPercentage: 0.68,
          categoryPercentage: 0.72,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            color: "rgba(71,85,105,0.95)",
            font: { size: tickFontSize, weight: "600" },
          },
        },
        y: {
          beginAtZero: true,
          suggestedMax: Math.max(...values, 1),
          ticks: {
            color: "rgba(71,85,105,0.85)",
            font: { size: tickFontSize },
          },
          grid: {
            color: "rgba(226,232,240,0.95)",
            borderDash: [4, 4],
          },
        },
      },
    },
    plugins: [
      {
        id: "valueLabels",
        afterDatasetsDraw(currentChart) {
          const { ctx } = currentChart;
          ctx.save();
          ctx.fillStyle = "#0f172a";
          ctx.font = `600 ${valueFontSize}px Inter, ui-sans-serif, system-ui, sans-serif`;
          ctx.textAlign = "center";
          ctx.textBaseline = "bottom";

          const meta = currentChart.getDatasetMeta(0);
          meta.data.forEach((bar, index) => {
            const value = values[index];
            if (value == null) return;
            ctx.fillText(String(value), bar.x, bar.y - 6);
          });
          ctx.restore();
        },
      },
    ],
  });
}

watch(
  () => [props.data, props.summary, props.loading],
  () => nextTick(renderChart),
  { deep: true, immediate: true }
);

onBeforeUnmount(() => {
  destroyChart();
});
</script>
